import {useEffect, useState} from "react";
import {useLingui} from "@lingui/react";
import {BASE_URL} from "../../consts/BaseUrl";
import {Layer, Source} from "@vis.gl/react-maplibre";
import {useDispatch, useSelector} from "react-redux";
import {addInteractiveLayer} from "../../store/reducers/map";
import httpReq from "../../services/httpReq";

const apiReportsURL = `${BASE_URL}/Report/GetList/`

function jitterCoordinate([lng, lat], index, total) {
    if (total === 1) return [lng, lat];
    const offset = 0.00001; // 1 metro
    const angle = (2 * Math.PI * index) / total;
    return [
        lng + offset * Math.cos(angle),
        lat + offset * Math.sin(angle)
    ];
}

const MapVideoReports = ({ project, video, tagValuesIds }) => {
    const {_} = useLingui();
    const dispatch = useDispatch();

    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [reports, setReports] = useState([]);

    const localStorageProjects = useSelector((state) => state.projects.openLocalStorage);
    const foundProject = (localStorageProjects && localStorageProjects.length > 0)
        ? localStorageProjects.find(p => p.id === project.id)
        : null;
    const localProject = { ...project, ...foundProject };
    const foundVideo = localProject.video
        ? localProject.video.find(v => v.id === localProject.id)
        : null;
    const localVideo = { ...video, ...foundVideo };

    const sourceID = 'reports-source-' + video.id;

    const clusterLayer = {
        id: 'clusters-' + video.id,
        type: 'circle',
        source: sourceID,
        filter: ['has', 'point_count'],
        paint: {
            'circle-color': ['step', ['get', 'point_count'], video.color, 100, video.color, 750, video.color],
            'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40]
        }
    };

    const clusterCountLayer = {
        id: 'cluster-count-' + video.id,
        type: 'symbol',
        source: sourceID,
        filter: ['has', 'point_count'],
        layout: {
            'text-field': '{point_count_abbreviated}',
            'text-size': 14
        }
    };

    const beginPointLayer = {
        id: 'begin-point-' + video.id,
        type: 'circle',
        source: sourceID,
        filter: [
            'all',
            ['!', ['has', 'point_count']],
            ['==', ['get', 'type'], 'begin']
        ],
        paint: {
            'circle-color': 'green',
            'circle-radius': 8,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff'
        }
    };

    const endPointLayer = {
        id: 'end-point-' + video.id,
        type: 'circle',
        source: sourceID,
        filter: [
            'all',
            ['!', ['has', 'point_count']],
            ['==', ['get', 'type'], 'end']
        ],
        paint: {
            'circle-color': 'red',
            'circle-radius': 8,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff'
        }
    };


    const labelLayer = {
        id: 'report-labels-' + video.id,
        type: 'symbol',
        source: sourceID,
        filter: ['!', ['has', 'point_count']],
        layout: {
            'text-field': ['get', 'title'],
            'text-size': 12,
            'text-offset': [0, 1.5],
            'text-anchor': 'top'
        },
        paint: {
            'text-color': '#000000'
        }
    };

    const f = [];
    const groupMap = {};

    reports.forEach(next => {
        //
        const beginCoords = [next.beginLongitude ?? next.longitude, next.beginLatitude ?? next.latitude];
        const beginKey = `begin-${beginCoords[0]},${beginCoords[1]}`;
        if (!groupMap[beginKey]) groupMap[beginKey] = [];
        groupMap[beginKey].push({
            type: 'begin',
            coords: beginCoords,
            title: next.endLongitude ? 'begin-' + next.title : next.title,
            report: next
        });

        //
        if (next.endLatitude && next.endLongitude) {
            const endCoords = [next.endLongitude, next.endLatitude];
            const endKey = `end-${endCoords[0]},${endCoords[1]}`;
            if (!groupMap[endKey]) groupMap[endKey] = [];
            groupMap[endKey].push({
                type: 'end',
                coords: endCoords,
                title: 'end-' + next.title,
                report: next
            });
        }
    });

    Object.values(groupMap).forEach(group => {
        group.forEach((item, idx) => {
            const jittered = jitterCoordinate(item.coords, idx, group.length);
            f.push({
                id: item.report.id + "-" + item.type + "-" + idx,
                type: "Feature",

                geometry: { type: "Point", coordinates: jittered },
                properties: {
                    type: item.type,
                    title: item.title,
                    report: { ...item.report }
                }
            });
        });
    });

    const geoJSON = {
        type: 'FeatureCollection',
        features: f
    };

    useEffect(() => {
        setIsLoaded(false);
    }, [tagValuesIds]);

    useEffect(() => {
        if (isLoading || isLoaded) return;
        if (localVideo.isOpen === false) {
            setIsLoaded(true);
            return;
        }
        setIsLoading(true);
        const body = {
            pageIndex: 0,
            pageSize: 0,
            videoFk: video.id,
            tagValuesFk: tagValuesIds ? tagValuesIds : []
        };
        httpReq('/Report/GetList', 'POST', body, true).then(res => {
            if (!res || res.responseAnyError) return;

            const items = [...res.responseData];
            setReports(items);
            setIsLoading(false);

            if (items.length > 0) {
                dispatch(addInteractiveLayer('clusters-' + video.id));
                dispatch(addInteractiveLayer('report-labels-' + video.id));
                dispatch(addInteractiveLayer('begin-point-' + video.id));
                dispatch(addInteractiveLayer('end-point-' + video.id));
            }
        });

        setIsLoaded(true);
    }, [localStorageProjects, isLoaded]);

    return (
        <Source
            key={sourceID}
            id={sourceID}
            type={'geojson'}
            data={geoJSON}
            cluster={true}
            clusterMaxZoom={10}
            clusterRadius={50}
        >
            <Layer {...beginPointLayer} />
            <Layer {...endPointLayer} />
            <Layer {...clusterLayer} />
            <Layer {...clusterCountLayer} />
            <Layer {...labelLayer} />
        </Source>
    )
}
export default MapVideoReports;
